// src/components/table/dataGridStyles.js
//
// One DataGridPro look, matching the reporting tables (the reference treatment is
// the campaign table in AnalyticsReportsCampaignConfig.jsx): a pale header strip
// with a small muted label, and hairline row separators, instead of the heavier
// grey block header the older grids used.
//
// Theme tokens are used wherever the palette has an equivalent, so the grids
// follow the palette instead of pinning hexes next to it:
//
//   #f8fafc (header strip)  -> grey[100]  (see the note on the header cell below)
//   #64748b (header label)  -> text.secondary          (grey[600])
//   1px rule                -> divider                 (grey[500] @ 20%)
//   row hairline            -> grey[500] @ 12%, a lighter rule inside the frame
//
// Adopted by the Dentally workspace grids first. Other DataGridPro consumers that
// could move onto this later: TaskListView, EmailTemplateDataGrid,
// WhatsappTemplateList and the superAdmin pages.
import { alpha } from '@mui/material/styles';

const rowRule = (theme) => alpha(theme.palette.grey[500], 0.12);

/** Base grid look. Spread it and override per grid where a screen genuinely differs. */
export const sharedDataGridSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '& .MuiDataGrid-columnHeaders': {
    borderRadius: '8px 8px 0 0',
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
  },
  // Two traps live in this one line.
  //
  // 1. The theme paints each header CELL (`columnHeader`, singular) with
  //    background.neutral — src/theme/overrides/components/data-grid.js — so
  //    colouring the `columnHeaders` container alone leaves that grey block
  //    showing through. The override lands in the same emitted class as this sx
  //    and wins at equal specificity, so `&&` doubles the root class to outrank
  //    it without reaching for !important.
  // 2. background.default is NOT a stable stand-in for the reporting #f8fafc:
  //    createContrast() rewrites it to grey[200] whenever themeContrast is
  //    'bold' (src/theme/options/contrast.js), which is exactly the grey we are
  //    trying to get rid of. grey[100] (#F9FAFB) is the palette's own pale
  //    neutral, one hex step off the reporting strip, and does not move.
  '&& .MuiDataGrid-columnHeader': { bgcolor: 'grey.100' },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: 12,
    fontWeight: 600,
    color: 'text.secondary',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: rowRule,
    fontSize: 13,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
  '& .MuiDataGrid-footerContainer': { borderTop: '1px solid', borderTopColor: 'divider' },
};

/**
 * Same look, with cell contents vertically centred — what every grid carrying an
 * avatar, chip or icon column wants, and what those grids each used to re-declare.
 */
export const denseDataGridSx = {
  ...sharedDataGridSx,
  '& .MuiDataGrid-cell': {
    ...sharedDataGridSx['& .MuiDataGrid-cell'],
    display: 'flex',
    alignItems: 'center',
  },
};
